import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { campaignLeadService } from '../services/campaignLead.service';
import { representativeService } from '../services/representative.service';
import { messageHandler } from '../services/message-handler.service';
import type { CampaignLead, CampaignLeadFilters as Filters } from '../types/campaignLead';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import { FormField, FormLabel, FormSelect, Button } from '../components/ui';
import CampaignLeadList from '../components/campaign-leads/CampaignLeadList';
import CampaignLeadDetails from '../components/campaign-leads/CampaignLeadDetails';
import CampaignLeadFilters from '../components/campaign-leads/CampaignLeadFilters';

function CampaignLeads() {
  const [viewingCampaignLead, setViewingCampaignLead] = useState<CampaignLead | null>(null);
  const [settingRepresentative, setSettingRepresentative] = useState<CampaignLead | null>(null);
  const [selectedRepresentativeId, setSelectedRepresentativeId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({});
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-leads', currentPage, filters],
    queryFn: () => campaignLeadService.getAll(currentPage, 20, filters),
  });

  const { data: representativesData } = useQuery({
    queryKey: ['representatives'],
    queryFn: () => representativeService.getAll(1, 1000),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => campaignLeadService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-leads'] });
      messageHandler.success('Lead de campanha arquivado com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const setRepresentativeMutation = useMutation({
    mutationFn: ({ id, representative_id }: { id: number; representative_id: number }) =>
      campaignLeadService.setRepresentative(id, representative_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-leads'] });
      messageHandler.success('Vendedor atribuído com sucesso!');
      closeRepresentativeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleFilter = (newFilters: Filters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const closeModal = () => {
    setViewingCampaignLead(null);
  };

  const closeRepresentativeModal = () => {
    setSettingRepresentative(null);
    setSelectedRepresentativeId('');
  };

  const handleArchive = (campaignLead: CampaignLead) => {
    messageHandler.confirm(
      'Arquivar Lead de Campanha',
      'Tem certeza que deseja arquivar este registro?',
      () => archiveMutation.mutate(campaignLead.id)
    );
  };

  const handleSetRepresentative = (campaignLead: CampaignLead) => {
    setSettingRepresentative(campaignLead);
    setSelectedRepresentativeId(campaignLead.representative_id?.toString() || '');
  };

  const handleSubmitRepresentative = (e: React.FormEvent) => {
    e.preventDefault();
    if (!settingRepresentative || !selectedRepresentativeId) return;

    setRepresentativeMutation.mutate({
      id: settingRepresentative.id,
      representative_id: parseInt(selectedRepresentativeId),
    });
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Gestão de Atendimentos"
          description="Gerencie leads, campanhas e atendimentos em um só lugar."
          icon={Users}
        />

        <CampaignLeadFilters
          filters={filters}
          onFilterChange={handleFilter}
          onClearFilters={handleClearFilters}
        />

        <CampaignLeadList
          campaignLeads={data?.data || []}
          onView={setViewingCampaignLead}
          onArchive={handleArchive}
          onSetRepresentative={handleSetRepresentative}
          isLoading={isLoading}
          pagination={data?.pagination}
          onPageChange={setCurrentPage}
        />

        <Modal isOpen={!!viewingCampaignLead} onClose={closeModal} title="Detalhes do Atendimento">
          {viewingCampaignLead && <CampaignLeadDetails campaignLead={viewingCampaignLead} />}
        </Modal>

        <Modal
          isOpen={!!settingRepresentative}
          onClose={closeRepresentativeModal}
          title="Atribuir Vendedor"
        >
          {settingRepresentative && (
            <form onSubmit={handleSubmitRepresentative}>
              <FormField>
                <FormLabel htmlFor="representative_id" required>
                  Vendedor
                </FormLabel>
                <FormSelect
                  id="representative_id"
                  name="representative_id"
                  value={selectedRepresentativeId}
                  onChange={e => setSelectedRepresentativeId(e.target.value)}
                  required
                >
                  <option value="">Selecione um vendedor</option>
                  {representativesData?.data?.map(rep => (
                    <option key={rep.id} value={rep.id}>
                      {rep.name}
                    </option>
                  ))}
                </FormSelect>
              </FormField>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <Button
                  type="submit"
                  isLoading={setRepresentativeMutation.isPending}
                  className="sm:col-start-2"
                >
                  Atribuir
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeRepresentativeModal}
                  className="sm:col-start-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          )}
        </Modal>
      </div>
    </div>
  );
}

export default CampaignLeads;
